import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import TurkishMainLayout from '@/components/layout/TurkishMainLayout';
import { Button } from '@/components/ui/button';
import { MessageSquare, Zap, Globe, Code, Hotel, Settings, Clock, Shield, CreditCard, CheckCircle, ArrowRight, Play, Mail, Phone, QrCodeIcon, CheckIcon } from 'lucide-react';

const TurkishLandingPage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://lobier.com/embed/lobier-chat.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <TurkishMainLayout>
      {/* Hero Section */}
      <section className="bg-black text-white pt-10 md:pt-20 relative">
        <img 
          src="/images/lobier-bg2.webp" 
          alt="Lobier AI Chat for Hotels" 
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
        />
        <div className="container mx-auto md:px-4 px-0 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Otelinizin Yapay Zekâ Asistanı
            </h1>
            <p className="text-lg md:text-xl mb-8 text-gray-100">
              Misafir sorularını anında yanıtlayan<br className='sm:hidden' /> 7/24 çalışan bir AI concierge
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-max mx-auto">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
                onClick={() => document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                Demo'yu İzle
              </Button>
              <Link to="/iletisim">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white bg-black text-white hover:bg-white hover:text-black px-8 py-3"
                >
                  Ücretsiz Kurulum İste
                </Button>
              </Link>
            </div>
          </div>
          <div className='flex flex-col gap-4 items-center justify-center max-w-max mx-auto mt-10 mb-20 pb-20'>
            <img src="/images/hotel-ai-chat-bot.png" alt="Lobier AI Chat for Hotels" className='w-full max-h-[500px] shadow-3xl rounded-2xl mb-20' />
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo-video" className="py-20 bg-gradient-to-b from-black to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Nasıl Çalışıyor?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              40 saniyede Lobier'in otel misafirlerinize nasıl yardım ettiğini görün
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg shadow-2xl overflow-hidden border border-slate-700">
              <video 
                className="w-full" 
                controls
                poster="/images/poster.webp"
              >
                <source src="/images/lobier-video.mp4" type="video/mp4" />
                Tarayıcınız video oynatmayı desteklemiyor.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-black to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Neden Lobier?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Otel misafirlerinize 7/24 kesintisiz hizmet verin.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <Clock className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-white">7/24 Çalışır</h3>
              <p className="text-gray-300">
                Gece yarısı bile misafirleriniz sorularını sorabilir ve anında yanıt alabilir.
              </p>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <Globe className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-white">Çok Dilli Destek</h3>
              <p className="text-gray-300">
                Türkçe, İngilizce, Almanca, Rusça ve daha fazla dilde misafirlerinizle konuşur.
              </p>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <div className='flex gap-2 items-start'>
                <QrCodeIcon className="h-12 w-12 text-green-500 mb-4" />
                <span className='bg-red-500 text-white text-sm px-2 py-1 rounded-full'>Yeni</span>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">QR Kod ile Kullanım</h3>
              <p className="text-gray-300">
                Otelinizdeki misafirleriniz QR kod ile Lobier'e erişebilir.
              </p>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <Zap className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-white">Anında Yanıt</h3>
              <p className="text-gray-300">
                Misafirleriniz beklemek zorunda kalmaz, sorularına saniyeler içinde yanıt alır.
              </p>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <CreditCard className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-white">Rezervasyon Artışı</h3>
              <p className="text-gray-300">
                Hızlı yanıtlar sayesinde daha fazla misafir rezervasyon yapar.
              </p>
            </div>
            
            <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
              <MessageSquare className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-white">Personel Yükünü Azaltır</h3>
              <p className="text-gray-300">
                Tekrarlayan soruları AI yanıtlar, personeliniz önemli işlere odaklanır.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-gradient-to-b from-black relative overflow-hidden">
        {/* Background Pattern */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">

            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-white">
              Lobier, Otelinizin Her Köşesinde
            </h2>

            <div className='flex flex-col md:flex-row gap-10 items-center justify-center max-w-xl mx-auto'>
                <div className="inline-flex items-center justify-center p-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl animate-float">
                  <QrCodeIcon className="h-20 w-20 text-white" />
                </div>
                <div className='flex flex-col gap-2 text-left text-white'>
                  <p className="flex items-center gap-2">
                    <CheckIcon className='h-4 w-4 text-green-500' />
                    QR Kodla Anında Erişim
                  </p>

                    <p className='flex items-center gap-2'>
                      <CheckIcon className='h-4 w-4 text-green-500' />
                      Wi-Fi şifresi, spa saatleri ya da restoran önerisi
                    </p>
                    <p className='flex items-center gap-2 mb-4'>
                      <CheckIcon className='h-4 w-4 text-green-500' />
                      Lobier, misafirlerinizin telefonunda 7/24 hazır.
                    </p>
                      <Link to="/iletisim" className='w-full sm:w-auto'>
                        <Button 
                          size="lg" 
                          variant="outline" 
                          className="border-white bg-black text-white hover:bg-white hover:text-black px-8 py-3"
                          >
                          Ücretsiz Kurulum İste
                          <ArrowRight className='h-4 w-4 animate-pulse' />
                        </Button>
                      </Link>
                  </div>
              </div>

          </div>
        </div>
      </section>


      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-b from-black to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Lobier Nasıl Çalışır?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              3 basit adımda otel websitenize AI asistanınızı ekleyin
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Otel Bilgilerinizi Ekleyin</h3>
                <p className="text-gray-300">
                Lobier, otelinizi tanır. Sık sorulanları, hizmetleri ve detayları sisteme kolayca eklersiniz.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">AI'nızı Özelleştirin</h3>
                <p className="text-gray-300">
                Lobier sizin gibi konuşur, sizin gibi görünür. Renkleri ve tonu markanıza uyacak şekilde özelleştirin.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">Websitenize Ekleyin</h3>
                <p className="text-gray-300">
                Tek satır kod ile sitenize ekleyin, Lobier misafirlerle hemen sohbet etmeye başlasın!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Sık Sorulan Sorular
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
              <h3 className="text-lg font-semibold mb-3 text-white">
                Lobier ücretli mi?
              </h3>
              <p className="text-gray-300">
                Lobier, otel büyüklüğünüze göre uygun fiyatlandırma sunar. Detaylı bilgi için bizimle iletişime geçin.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
              <h3 className="text-lg font-semibold mb-3 text-white">
                Hangi dilleri destekliyor?
              </h3>
              <p className="text-gray-300">
                Şu anda Türkçe, İngilizce, Almanca ve Rusça dillerini destekliyoruz. Daha fazla dil yakında eklenecek.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
              <h3 className="text-lg font-semibold mb-3 text-white">
                Kurulum ne kadar sürer?
              </h3>
              <p className="text-gray-300">
                Otel bilgilerinizi girdikten sonra, tek satır kod ile websitenize ekleyebilirsiniz. Toplam süre 10 dakika.
              </p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
              <h3 className="text-lg font-semibold mb-3 text-white">
              Nereden eğitiliyor bu asistan?
              </h3>
              <p className="text-gray-300">
              Hiç teknik bilgiye gerek yok. Sadece otelinizin fact sheet dosyasını yüklüyorsunuz, gerisini biz hallediyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Demo Button */}
      <div className='fixed bottom-10 right-10 z-50'>
        <a href="https://lobier.com/demo/" target="_blank" rel="noopener noreferrer">
          <Button 
            size="lg" 
            className="bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 w-12 h-12 p-0"
          >
            <MessageSquare className="h-8 w-8" />
          </Button>
        </a>
      </div>

    </TurkishMainLayout>
  );
};

export default TurkishLandingPage; 